import { ChangeEvent, useMemo } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { resources } from "@constants/hardcoded-strings";
import { TEXT_POSITION_OPTIONS } from "@constants/text-position-options";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { SelectOption } from "@idw-types/common";
import { AXNUIContent, AXNUIContentFooter, AXNUITheme, AXNUIThemeFooter } from "@iddataweb/idw-common-ui/types";
import { InputFormGroup } from "../../../../../common/input-form-group/input-form-group";
import { SelectFormGroup } from "../../../../../common/select-form-group/select-form-group";
import { ColorPickerFormGroup } from "../../../../../common/color-picker-form-group/color-picker-form-group";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { PageLevelTypes } from "@enums/page-level-types";

type ThemingFooterTextSectionProps = {
    layout: LayoutTypes;
    labelWidth: LabelWidthTypes;
    isDisabled: boolean;
    axnuiContent: AXNUIContent;
    axnuiTheme: AXNUITheme;
    inheritedAXNUIContent: AXNUIContent;
    inheritedAXNUITheme: AXNUITheme;
    pageLevel: PageLevelTypes;
    updateAXNUIContentFooterFieldValue: (key: keyof AXNUIContentFooter, value: string) => void;
    updateAXNUIThemeFooterFieldValue: (key: keyof AXNUIThemeFooter, value: string) => void;
};

export const ThemingFooterTextSection = (props: ThemingFooterTextSectionProps) => {
    const {
        layout,
        labelWidth,
        isDisabled,
        axnuiContent,
        axnuiTheme,
        inheritedAXNUIContent,
        inheritedAXNUITheme,
        pageLevel,
        updateAXNUIContentFooterFieldValue,
        updateAXNUIThemeFooterFieldValue
    } = props;

    const handleFooterTextChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentFooterFieldValue("text", event.target.value);
    };

    const handleResetFooterText = () => {
        updateAXNUIContentFooterFieldValue("text", inheritedAXNUIContent.footer.text);
    };

    const handleFooterTextPositionChange = (newValue: SelectOption) => {
        updateAXNUIThemeFooterFieldValue("alignText", newValue.label);
    };

    const handleResetFooterTextPosition = () => {
        updateAXNUIThemeFooterFieldValue("alignText", inheritedAXNUITheme.footer.alignText);
    };

    const handleFooterTextColorChange = (newValue: string) => {
        updateAXNUIThemeFooterFieldValue("textColor", newValue);
    };

    const handleResetFooterTextColor = () => {
        updateAXNUIThemeFooterFieldValue("textColor", inheritedAXNUITheme.footer.textColor);
    };

    const selectedFooterTextPositionOption = useMemo(() => {
        if (axnuiTheme.footer.alignText) {
            return TEXT_POSITION_OPTIONS.find(po => po.label === axnuiTheme.footer.alignText);
        }
        return null;
    }, [axnuiTheme.footer.alignText]);

    return (
        <div>
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUIContent.footer.text}
                currentThemingPropertyValue={axnuiContent.footer.text}
                pageLevel={pageLevel}
            >
                <InputFormGroup
                    controlId={"theming-footer-footer-text-input"}
                    automationId={"theming-footer-footer-text-input"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.footer.sections.footerText.inputs.footerText.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.footer.sections.footerText.inputs.footerText.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleFooterTextChange}
                    value={axnuiContent.footer.text || ""}
                    disabled={isDisabled}
                    isRequired={false}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.footer.sections.footerText.inputs.footerText.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetFooterText}
                />
            </ThemingFormGroupWrapper>

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUITheme.footer.alignText}
                currentThemingPropertyValue={axnuiTheme.footer.alignText}
                pageLevel={pageLevel}
            >
                <SelectFormGroup
                    controlId={"theming-footer-footer-text-postition-select-input"}
                    automationId={"theming-footer-footer-text-position-select-input"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.footer.sections.footerText.inputs.footerTextPosition.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.footer.sections.footerText.inputs.footerTextPosition.label}
                    disabled={isDisabled}
                    isClearable={false}
                    isRequired={true}
                    isSearchable={false}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleFooterTextPositionChange}
                    options={TEXT_POSITION_OPTIONS}
                    selectedOption={selectedFooterTextPositionOption}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.footer.sections.footerText.inputs.footerTextPosition.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetFooterTextPosition}
                />
            </ThemingFormGroupWrapper>

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUITheme.footer.textColor}
                currentThemingPropertyValue={axnuiTheme.footer.textColor}
                pageLevel={pageLevel}
            >
                <ColorPickerFormGroup
                    controlId={"theming-footer-footer-text-color-picker"}
                    automationId={"theming-footer-footer-text-color-picker"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.footer.sections.footerText.inputs.footerTextColor.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.footer.sections.footerText.inputs.footerTextColor.label}
                    disabled={isDisabled}
                    isRequired={true}
                    layout={layout}
                    labelWidth={labelWidth}
                    value={axnuiTheme.footer.textColor || ""}
                    onChange={handleFooterTextColorChange}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.footer.sections.footerText.inputs.footerTextColor.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetFooterTextColor}
                />
            </ThemingFormGroupWrapper>
        </div>
    );
};